/**
 * Схема "Архив от продавца (zip)" (KAN-677): один архив, внутри папка tdata
 * Telegram Desktop (KAN-675) или файлы .session и .json; что именно, сервер
 * определяет сам (import-archive/, в ответе detected и warning). Сессию и
 * параметры устройства сервер берёт из архива, JSON продавца отдельно не нужен.
 * Лимит на клиенте: zip до 50 МБ.
 * KAN-676: порядок полей как у остальных схем: архив, общие поля, продавец,
 * видео (слот от мастера), галочки, кнопка внизу.
 * KAN-722: выбранный zip сразу разбирается (archive/inspect/): карточка с
 * форматом, номером и 2FA, метка и галочка 2FA предзаполняются, их можно сменить.
 * KAN-739: если номер уже в кабинете (inspect вернул duplicate), карточка
 * показывает красное сообщение и кнопка подключения неактивна.
 * KAN-776: файлов можно выбрать несколько или перетащить (ArchiveDropZone);
 * два и более - режим партии (ImportArchiveBatch: план, общие поля, ход),
 * один - прежний одиночный импорт. Поля видео (videoFields) в партии не
 * применяются: видео привязано к одному аккаунту.
 */
import { useState, type ReactNode } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { ArchiveDropZone } from '@/components/telegram/ArchiveDropZone';
import { ArchiveInspectCard } from '@/components/telegram/ArchiveInspectCard';
import { ImportArchiveBatch } from '@/components/telegram/ImportArchiveBatch';
import { ConnectCommonFields } from '@/components/telegram/ConnectCommonFields';
import { ConnectResultStep } from '@/components/telegram/ConnectResultStep';
import { ConnectSellerFields } from '@/components/telegram/ConnectSellerFields';
import { PurchaseCostField } from '@/components/telegram/PurchaseCostField';
import { ImportFlagsFields } from '@/components/telegram/ImportFlagsFields';
import {
  DEFAULT_COMMON,
  DEFAULT_IMPORT_FLAGS,
  applyArchiveInspect,
  buildArchiveInspectFormData,
  buildImportArchiveFormData,
  describeApiError,
  resolveLabel,
  skipSpamCheckFromFlag,
  validateArchiveZip,
  validateCommonProxy,
  validatePurchaseCost,
  type CommonValues,
  type ImportFlags,
} from '@/lib/telegramConnect';
import { proxyToUrl } from '@/lib/telegramProxy';
import { telegramAccountsApi } from '@/services/api/telegramAccounts';
import type {
  ArchiveInspectResult,
  ImportResult,
  TelegramAccount,
} from '@/types/telegramAccount';

export { ARCHIVE_HINT, ARCHIVE_LABEL } from '@/components/telegram/ArchiveDropZone';

interface Props {
  onConnected: (account: TelegramAccount) => void;
  onClose: () => void;
  /** KAN-648: поля видеозаписи аккаунта; состояние живёт в мастере. */
  videoFields?: ReactNode;
  /** KAN-752: выбранная кампания (оператор); пусто - не передаётся. */
  operatorId?: string;
}

export function ImportArchiveForm({
  onConnected,
  onClose,
  videoFields,
  operatorId,
}: Props): JSX.Element {
  const [common, setCommon] = useState<CommonValues>(DEFAULT_COMMON);
  const [zipFile, setZipFile] = useState<File | null>(null);
  const [flags, setFlags] = useState<ImportFlags>(DEFAULT_IMPORT_FLAGS);
  const [fileError, setFileError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  const [inspected, setInspected] = useState<ArchiveInspectResult | null>(null);
  const [inspectError, setInspectError] = useState<string | null>(null);
  // KAN-776: два и более файла - партия; null - одиночный режим
  const [batchFiles, setBatchFiles] = useState<File[] | null>(null);
  const [batchStarted, setBatchStarted] = useState(false);
  const [batchPending, setBatchPending] = useState(false);

  const inspectMutation = useMutation({
    mutationFn: (file: File) =>
      telegramAccountsApi.inspectArchive(buildArchiveInspectFormData(file, operatorId)),
  });

  const inspect = (file: File) => {
    // колбэки на mutate: срабатывают только для последнего выбранного файла
    inspectMutation.mutate(file, {
      onSuccess: (res) => {
        setInspected(res);
        setCommon((prev) => applyArchiveInspect(prev, res));
        if (res.two_fa_present) setFlags((prev) => ({ ...prev, had2fa: true }));
      },
      onError: (err: unknown) =>
        setInspectError(describeApiError(err, 'ошибка сервера')),
    });
  };

  const importMutation = useMutation({
    mutationFn: (file: File) =>
      telegramAccountsApi.importArchive(
        buildImportArchiveFormData({
          archive: file,
          ...resolveLabel(common),
          proxy: proxyToUrl(common.proxy),
          proxyId: common.proxyId,
          enabled: common.enabled,
          outreach: common.outreach,
          skipSpamCheck: skipSpamCheckFromFlag(flags.checkSpam),
          had2fa: flags.had2fa,
          sellerId: common.sellerId,
          purchaseCost: common.purchaseCost,
          operatorId,
        })
      ),
    onSuccess: (res) => {
      setZipFile(null);
      setResult(res);
      onConnected(res.account);
    },
    onError: (err: unknown) => {
      setError(describeApiError(err, 'Не удалось подключить аккаунт из архива'));
    },
  });

  const pickZip = (file: File | null) => {
    setZipFile(null);
    setFileError(null);
    setInspected(null);
    setInspectError(null);
    inspectMutation.reset();
    if (!file) return;
    const problem = validateArchiveZip(file);
    if (problem) setFileError(problem);
    else {
      setZipFile(file);
      inspect(file);
    }
  };

  const pickFiles = (files: File[]) => {
    if (files.length >= 2) {
      pickZip(null);
      setBatchFiles(files);
      return;
    }
    setBatchFiles(null);
    pickZip(files[0] ?? null);
  };

  if (result) {
    return (
      <ConnectResultStep
        account={result.account}
        scheme="archive"
        spam={result.spam}
        selfMessage={result.self_message}
        warning={result.warning}
        detected={result.detected}
        onDone={onClose}
      />
    );
  }

  // пустые поля прокси = без прокси, ошибка только у заполненных
  const proxyError = validateCommonProxy(common);
  const costError = validatePurchaseCost(common.purchaseCost);
  // KAN-739: номер уже в кабинете, повторный вход в ту же сессию убьёт аккаунт
  const isDuplicate = !!inspected?.duplicate;
  const canSubmit =
    !!zipFile &&
    !fileError &&
    !proxyError &&
    !costError &&
    !isDuplicate &&
    !inspectMutation.isPending &&
    !importMutation.isPending;

  if (batchFiles) {
    return (
      <div className="space-y-4">
        {!batchStarted && <ArchiveDropZone onFiles={pickFiles} disabled={batchPending} />}
        <ImportArchiveBatch
          key={batchFiles.map((f) => f.name).join('|')}
          files={batchFiles}
          operatorId={operatorId}
          onClose={onClose}
          onReset={() => setBatchFiles(null)}
          onStarted={() => setBatchStarted(true)}
          onPendingChange={setBatchPending}
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <ArchiveDropZone onFiles={pickFiles} disabled={importMutation.isPending} />
        {fileError && (
          <p className="text-xs text-red-600" data-testid="archive-zip-error">
            {fileError}
          </p>
        )}
        {zipFile && (
          <ArchiveInspectCard
            result={inspected}
            pending={inspectMutation.isPending}
            error={inspectError}
          />
        )}
      </div>

      <ConnectCommonFields
        values={common}
        proxyError={proxyError}
        disabled={importMutation.isPending}
        onChange={(patch) => setCommon((prev) => ({ ...prev, ...patch }))}
      />

      <ConnectSellerFields
        sellerId={common.sellerId}
        disabled={importMutation.isPending}
        onChange={(sellerId) => setCommon((prev) => ({ ...prev, sellerId }))}
      />

      <PurchaseCostField
        value={common.purchaseCost}
        disabled={importMutation.isPending}
        onChange={(purchaseCost) => setCommon((prev) => ({ ...prev, purchaseCost }))}
      />

      {videoFields}

      <ImportFlagsFields
        idPrefix="archive"
        values={flags}
        disabled={importMutation.isPending}
        onChange={(patch) => setFlags((prev) => ({ ...prev, ...patch }))}
      />

      {error && (
        <Alert variant="destructive" data-testid="connect-error">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <div className="flex justify-end">
        <Button
          data-testid="connect-archive-button"
          disabled={!canSubmit}
          onClick={() => {
            if (!zipFile) return;
            setError(null);
            importMutation.mutate(zipFile);
          }}
        >
          {importMutation.isPending && (
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          )}
          Подключить
        </Button>
      </div>
    </div>
  );
}
